import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, RefreshCw, CheckCircle2, Settings2, Eye } from "lucide-react";
import { toast } from "sonner";
import { getArrearsSyncSettings, saveArrearsSyncSettings, syncArrearsFromLoanDisk, getArrearsSyncJob } from "@/lib/api";

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function errText(e) {
  return e?.response?.data?.detail || e?.message || "Unknown error";
}

export function SyncArrearsDialog({ open, onClose, onSynced }) {
  const [cfg, setCfg] = useState(null);
  const [minDays, setMinDays] = useState("");
  const [maxDays, setMaxDays] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [draft, setDraft] = useState({ min_days: 1, max_days: "", by_weekday: {} });
  const [busy, setBusy] = useState(null); // "preview" | "sync" | "save"
  const [result, setResult] = useState(null);
  const [phaseMsg, setPhaseMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    setResult(null);
    getArrearsSyncSettings().then((r) => {
      setCfg(r);
      setMinDays(String(r.today.min_days));
      setMaxDays(r.today.max_days != null ? String(r.today.max_days) : "");
      setDraft({ min_days: r.settings.min_days ?? 1, max_days: r.settings.max_days ?? "", by_weekday: r.settings.by_weekday || {}, safety_net_hour: r.settings.safety_net_hour ?? "" });
    }).catch((e) => toast.error("Could not load sync settings: " + errText(e)));
  }, [open]);

  const overrideActive = cfg && (String(cfg.today.min_days) !== minDays || String(cfg.today.max_days ?? "") !== maxDays);

  const run = async (dry) => {
    try {
      setBusy(dry ? "preview" : "sync");
      setPhaseMsg("Starting…");
      const start = await syncArrearsFromLoanDisk({
        dry_run: dry,
        use_override: !!overrideActive,
        min_days: minDays === "" ? undefined : Number(minDays),
        max_days: maxDays === "" ? null : Number(maxDays),
      });
      if (start.already_running) toast.info("A sync is already running for this desk — attaching to it");
      let job = start;
      const deadline = Date.now() + 6 * 60 * 1000;
      while (job.status === "running" && Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2500));
        job = await getArrearsSyncJob(start.job_id);
        setPhaseMsg(job.message || job.phase || "Working…");
      }
      if (job.status === "error") {
        const err = new Error(job.error || "Sync failed"); err.response = { status: job.http_status, data: { detail: job.error } }; throw err;
      }
      if (job.status !== "done") throw new Error("Sync is taking longer than expected — check the history list in a minute");
      const r = job.result || {};
      setResult({ ...r, dry_run: dry });
      if (!dry) {
        toast.success(`Synced ${r.parsed_rows} loans from LoanDisk · ${r.created} new · ${r.refreshed} refreshed${r.off_list_flagged ? ` · ${r.off_list_flagged} fell off` : ""}`, { duration: 8000 });
        onSynced?.();
      }
    } catch (e) {
      const status = e?.response?.status;
      toast.error((status === 429 ? "LoanDisk is rate-limiting reports: " : "Sync failed: ") + errText(e), { duration: 10000 });
    } finally {
      setBusy(null);
      setPhaseMsg("");
    }
  };

  const saveSettings = async () => {
    try {
      setBusy("save");
      const byWd = Object.fromEntries(Object.entries(draft.by_weekday).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)]));
      const r = await saveArrearsSyncSettings({ min_days: Number(draft.min_days), max_days: draft.max_days === "" ? null : Number(draft.max_days), by_weekday: byWd,
        safety_net_hour: draft.safety_net_hour === "" || draft.safety_net_hour == null ? null : Number(draft.safety_net_hour) });
      setCfg(r);
      setMinDays(String(r.today.min_days));
      setMaxDays(r.today.max_days != null ? String(r.today.max_days) : "");
      setShowSettings(false);
      toast.success("Sync settings saved");
    } catch (e) {
      toast.error("Could not save: " + errText(e));
    } finally {
      setBusy(null);
    }
  };

  if (!open) return null;
  const f = result?.api_filter_stats || result?.parse_stats || {};
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="ageing-sync-dialog">
        <SheetHeader>
          <SheetTitle>Sync arrears report from LoanDisk</SheetTitle>
          <SheetDescription>
            Pulls LoanDisk&apos;s Loan Arrears Aging report over the API and runs it through the same matching as the spreadsheet upload. Enter today&apos;s payments in LoanDisk first, then sync.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-4">
          <div className="rounded-md border bg-slate-50 p-3 text-xs space-y-2" data-testid="ageing-sync-today">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-800">Today&apos;s threshold {cfg ? `(${cfg.today.weekday.toUpperCase()}, ${cfg.region})` : ""}</span>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={() => setShowSettings((v) => !v)} data-testid="ageing-sync-settings-toggle">
                <Settings2 className="w-3 h-3 mr-1" /> {showSettings ? "Hide settings" : "Sync settings"}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px]">Days in arrears from</Label>
                <Input type="number" min={0} value={minDays} onChange={(e) => setMinDays(e.target.value)} className="h-8 text-xs" data-testid="ageing-sync-min-days" />
              </div>
              <div>
                <Label className="text-[11px]">to (blank = no cap)</Label>
                <Input type="number" min={0} value={maxDays} onChange={(e) => setMaxDays(e.target.value)} className="h-8 text-xs" placeholder="∞" data-testid="ageing-sync-max-days" />
              </div>
            </div>
            {overrideActive && <div className="text-amber-700" data-testid="ageing-sync-override-note">Override for this run only — saved default is day {cfg.today.min_days}{cfg.today.max_days != null ? `–${cfg.today.max_days}` : "+"}.</div>}
          </div>

          {showSettings && cfg && (
            <div className="rounded-md border p-3 text-xs space-y-3" data-testid="ageing-sync-settings">
              <div className="font-semibold text-slate-800">Default threshold for {cfg.region}</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">Default min days</Label>
                  <Input type="number" min={0} value={draft.min_days} onChange={(e) => setDraft({ ...draft, min_days: e.target.value })} className="h-8 text-xs" data-testid="ageing-sync-default-min" />
                </div>
                <div>
                  <Label className="text-[11px]">Default max days</Label>
                  <Input type="number" min={0} value={draft.max_days} onChange={(e) => setDraft({ ...draft, max_days: e.target.value })} className="h-8 text-xs" placeholder="∞" data-testid="ageing-sync-default-max" />
                </div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground mb-1">Per-weekday min days (blank = use default). e.g. AU: Mon 3, Tue 2.</div>
                <div className="grid grid-cols-7 gap-1">
                  {WEEKDAYS.map((d) => (
                    <div key={d} className="text-center">
                      <div className="text-[10px] uppercase text-muted-foreground">{d}</div>
                      <Input type="number" min={0} value={draft.by_weekday[d] ?? ""} placeholder="–"
                        onChange={(e) => setDraft({ ...draft, by_weekday: { ...draft.by_weekday, [d]: e.target.value } })}
                        className="h-7 text-xs px-1 text-center" data-testid={`ageing-sync-wd-${d}`} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded border border-slate-200 bg-slate-50 p-2">
                <Label className="text-[11px]">Safety net — auto-sync if nobody has synced or uploaded by (desk-local hour, blank = off)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="number" min={0} max={23} value={draft.safety_net_hour ?? ""} placeholder="off"
                    onChange={(e) => setDraft({ ...draft, safety_net_hour: e.target.value })} className="h-8 w-20 text-xs" data-testid="ageing-sync-safety-hour" />
                  <span className="text-[11px] text-slate-600">:00 {cfg.region === "AUS" ? "Sydney" : "London"} time, weekdays — posts to the desk Slack channel when it runs{cfg.settings.last_safety_net?.at ? ` · last ran ${new Date(cfg.settings.last_safety_net.at).toLocaleString("en-GB")}` : ""}</span>
                </div>
              </div>
              <Button size="sm" onClick={saveSettings} disabled={busy === "save"} className="w-full h-8 text-xs" data-testid="ageing-sync-settings-save">
                {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null} Save defaults
              </Button>
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => run(true)} disabled={!!busy || !cfg} className="flex-1" data-testid="ageing-sync-preview">
              {busy === "preview" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <Eye className="w-3.5 h-3.5 mr-2" />} Preview
            </Button>
            <Button onClick={() => run(false)} disabled={!!busy || !cfg} className="flex-1" data-testid="ageing-sync-submit">
              {busy === "sync" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <RefreshCw className="w-3.5 h-3.5 mr-2" />} Sync now
            </Button>
          </div>
          {busy && busy !== "save" && <div className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="ageing-sync-progress"><Loader2 className="w-3 h-3 animate-spin" />{phaseMsg || "Working…"} (can take 1–2 minutes)</div>}

          {result && (
            <div className={`rounded-md border p-3 space-y-1.5 text-xs ${result.dry_run ? "border-sky-200 bg-sky-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="ageing-sync-result">
              <div className="font-semibold flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                {result.dry_run ? "Preview" : "Synced"}: {result.parsed_rows} loans at day {f.min_days}{f.max_days != null ? `–${f.max_days}` : "+"} from {f.api_rows_total} rows (all branches)
              </div>
              <ul className="space-y-0.5 pl-5 list-disc opacity-90">
                <li>{f.other_branch} other-branch rows · {f.below_min_days} below threshold{f.above_max_days ? ` · ${f.above_max_days} above cap` : ""}</li>
                {result.dry_run
                  ? <li>{result.would_create_or_refresh} cases would be created or refreshed · {result.match_stats?.unmatched ?? 0} without a Monday match</li>
                  : <li>{result.created} new cases · {result.refreshed} refreshed{result.off_list_flagged ? ` · ${result.off_list_flagged} fell off the list` : ""}</li>}
                {result.totals && <li>Arrears {Number(result.totals.arrears_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} · balance {Number(result.totals.balance_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</li>}
                {result.loandisk_report_id && <li>LoanDisk report #{result.loandisk_report_id}{result.loandisk_generated_at ? ` · built ${result.loandisk_generated_at}` : ""}</li>}
                {result.dry_run && <li className="text-sky-800 font-medium">Nothing was written — click Sync now to apply.</li>}
              </ul>
            </div>
          )}
          <div className="text-[11px] text-muted-foreground">LoanDisk builds one new report every 15 minutes (shared by UK &amp; AU); a fresh report inside that window reuses the last one. The spreadsheet upload stays available as a fallback.</div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
